import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, ExternalLink, ChevronLeft, AlertTriangle, CloudOff, CloudUpload, LogOut } from 'lucide-react';

import './mobile.css';
import StatusBar from './StatusBar';
import ScreenOnboarding from './screens/ScreenOnboarding';
import ScreenHome from './screens/ScreenHome';
import ScreenConducta from './screens/ScreenConducta';
import ScreenVariables from './screens/ScreenVariables';
import ScreenEvidencia from './screens/ScreenEvidencia';
import ScreenConfirmacion from './screens/ScreenConfirmacion';
import ScreenMisRegistros from './screens/ScreenMisRegistros';

import { calcularNivel, APP_MODES, ESTADO_INICIAL, DOMAINS } from '../data/observationEngine';
import { trackEvent } from '../lib/tracking';
import { useAuth } from '../context/AuthContext';
import {
  newUUID,
  buildObservationPayload,
  enqueueObservation,
  processQueue,
  getQueueStats,
  purgeLegacyData,
  listMisObservaciones,
  updateObservacion,
  marcarErroneo as marcarErroneoApi,
} from '../data/supabaseObservations';

// Solo claves de UI local (NO de datos de observaciones, eso vive en Supabase).
const UI_KEYS = {
  draft: 'preventiapp_mobile_draft_v3',
  onboardingSeen: 'preventiapp_mobile_onboarding_seen_v1',
  mode: 'preventiapp_mobile_mode_v1',
  appMode: 'preventiapp_mobile_app_mode_v1',
};

const QUICK_DEFAULTS = {
  curso: 'No identificado',
  estudiante: '',
  frecuencia: 'primera_vez',
  duracion: 'episodio_unico',
  participantes: 'sin_identificar',
  fuente: 'observacion_directa',
};

const EMPTY_DRAFT = {
  client_id: null,
  dominio: null,
  conducta: null,
  curso: '',
  estudiante: '',
  contexto: '',
  frecuencia: '',
  intensidad: '',
  duracion: '',
  participantes: '',
  fuente: '',
  evidencia: { nota: '', audioSeconds: 0, fotos: [], stagedFotos: [] },
};

const persistSafe = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* no-op */ } };
const readSafe = (k, f) => { try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : f; } catch { return f; } };

// Lee items de la cola offline para mostrarlos junto a los sincronizados.
function readOfflineQueue() {
  try { return JSON.parse(localStorage.getItem('preventiapp_offline_queue_v1') || '[]'); } catch { return []; }
}

// Adapta una fila de Supabase al shape que las screens esperan (compat Sprint 2).
function adaptRowToScreenShape(row) {
  const dom = DOMAINS.find((d) => d.id === row.dominio_code) || { id: row.dominio_code, name: row.dominio_code };
  return {
    id: row.id,
    client_id: row.client_id,
    createdAt: row.creado_en,
    updatedAt: row.actualizado_en,
    session: { nombre: 'me' },                  // RLS ya filtra propias → siempre del usuario actual
    mode: row.flujo_captura,
    appMode: row.modo_registro,
    estado: row.estado,
    sync_estado: 'sincronizado',
    versions: [],                                // se cargan on-demand vía listVersiones
    dominio: { id: dom.id, name: dom.name },
    conducta: { code: row.conducta_code, label: row.conducta_code }, // label real se resuelve en screen
    curso: row.curso_texto || '',
    estudiante: row.estudiante || '',
    contexto: row.contexto || '',
    frecuencia: row.frecuencia || '',
    intensidad: row.intensidad || '',
    duracion: row.duracion || '',
    participantes: row.participantes || '',
    fuente: row.fuente || '',
    evidencia: {
      nota: row.observaciones_evidencias?.find((e) => e.tipo === 'texto')?.contenido_texto || '',
      audioSeconds: 0,
      fotos: (row.observaciones_evidencias || []).filter((e) => e.tipo === 'foto').map((_) => 'linear-gradient(135deg, #EEEAFE 0%, #DCE7FF 100%)'),
    },
  };
}

// Convierte un item de la cola offline en el shape de pantalla con sync_estado.
function adaptQueueItemToScreenShape(item) {
  const p = item.payload || {};
  const dom = DOMAINS.find((d) => d.id === p.dominio_code) || { id: p.dominio_code, name: p.dominio_code };
  return {
    id: `pending-${p.client_id}`,
    client_id: p.client_id,
    createdAt: item.queued_at,
    updatedAt: item.queued_at,
    session: { nombre: 'me' },
    mode: p.flujo_captura,
    appMode: p.modo_registro,
    estado: 'registrado',
    sync_estado: item.estado_sync === 'fallido' ? 'fallido' : 'pendiente',
    sync_retries: item.retries || 0,
    versions: [],
    dominio: { id: dom.id, name: dom.name },
    conducta: { code: p.conducta_code, label: p.conducta_code },
    curso: p.curso_texto || '',
    estudiante: p.estudiante || '',
    contexto: p.contexto || '',
    frecuencia: p.frecuencia || '',
    intensidad: p.intensidad || '',
    duracion: p.duracion || '',
    participantes: p.participantes || '',
    fuente: p.fuente || '',
    evidencia: { nota: item.evidencia?.nota || '', audioSeconds: 0, fotos: [] },
  };
}

const MobileApp = () => {
  const { profile, session, signOut } = useAuth();
  const onboardingSeen = readSafe(UI_KEYS.onboardingSeen, false);

  const [screen, setScreen] = useState(onboardingSeen ? 'home' : 'onboarding');
  const [draft, setDraft] = useState(() => ({ ...EMPTY_DRAFT, ...(readSafe(UI_KEYS.draft, {}) || {}) }));
  const [mode, setMode] = useState(() => readSafe(UI_KEYS.mode, 'completo'));
  const [appMode, setAppMode] = useState(() => readSafe(UI_KEYS.appMode, 'operativo'));
  const [time, setTime] = useState('09:24');
  const [pendingAppMode, setPendingAppMode] = useState(null);

  // Estado server-side
  const [observaciones, setObservaciones] = useState([]);     // operativo
  const [observacionesInduccion, setObservacionesInduccion] = useState([]);
  const [loadingObs, setLoadingObs] = useState(false);
  const [syncState, setSyncState] = useState({ pendientes: 0, online: typeof navigator !== 'undefined' ? navigator.onLine : true });

  const userSession = useMemo(() => ({
    nombre: profile?.nombre_completo || session?.user?.email || 'Usuario',
    rol: profile?.rol || 'docente',
  }), [profile, session]);

  // Limpia datos mock de Sprint 2 al primer mount con sesión activa.
  useEffect(() => {
    const purged = purgeLegacyData();
    if (purged > 0) console.info(`[Etapa6] Purgados ${purged} namespaces legacy Sprint 2`);
  }, []);

  // Reloj
  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setTime(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`);
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => clearInterval(i);
  }, []);

  // Persistencia UI local
  useEffect(() => { persistSafe(UI_KEYS.draft, { ...draft, evidencia: { ...draft.evidencia, stagedFotos: [] } }); }, [draft]);
  useEffect(() => { persistSafe(UI_KEYS.mode, mode); }, [mode]);
  useEffect(() => { persistSafe(UI_KEYS.appMode, appMode); }, [appMode]);

  // Carga de observaciones desde Supabase para el modo activo + items pendientes en cola
  const reloadObservaciones = useCallback(async () => {
    if (!profile?.id) return;
    setLoadingObs(true);
    try {
      const [op, ind] = await Promise.all([
        listMisObservaciones('operativo'),
        listMisObservaciones('induccion'),
      ]);
      const queue = readOfflineQueue();
      const pendientesOp = queue.filter((i) => i.payload?.modo_registro === 'operativo' && i.payload?.autor_id === profile.id).map(adaptQueueItemToScreenShape);
      const pendientesInd = queue.filter((i) => i.payload?.modo_registro === 'induccion' && i.payload?.autor_id === profile.id).map(adaptQueueItemToScreenShape);
      setObservaciones([...pendientesOp, ...op.map(adaptRowToScreenShape)]);
      setObservacionesInduccion([...pendientesInd, ...ind.map(adaptRowToScreenShape)]);
    } catch (e) {
      console.error('[Etapa6] listMisObservaciones:', e?.message || e);
    } finally {
      setLoadingObs(false);
    }
  }, [profile?.id]);

  useEffect(() => { reloadObservaciones(); }, [reloadObservaciones]);

  // Sync queue: al montar, al volver online, y cada 30s si hay pendientes.
  const refreshQueueStats = useCallback(() => setSyncState((s) => ({ ...s, ...getQueueStats() })), []);
  const flush = useCallback(async () => {
    const res = await processQueue();
    setSyncState({ online: !!res.online, pendientes: getQueueStats().pendientes });
    if (res.processed > 0) reloadObservaciones();
  }, [reloadObservaciones]);

  useEffect(() => {
    refreshQueueStats();
    flush();
    const onOnline = () => { setSyncState((s) => ({ ...s, online: true })); flush(); };
    const onOffline = () => setSyncState((s) => ({ ...s, online: false }));
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const t = setInterval(() => { if (navigator.onLine && getQueueStats().pendientes > 0) flush(); }, 30000);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      clearInterval(t);
    };
  }, [flush, refreshQueueStats]);

  const updateDraft = (updater) =>
    setDraft((d) => typeof updater === 'function' ? updater(d) : { ...d, ...updater });

  const go = (next) => {
    trackEvent('mobile_nav', { from: screen, to: next, mode });
    setScreen(next);
  };

  const completeOnboarding = () => {
    persistSafe(UI_KEYS.onboardingSeen, true);
    trackEvent('mobile_onboarding_completed', {});
    go('home');
  };

  const handleSelectDomain = (dominio) => {
    updateDraft({ dominio, conducta: draft.dominio?.id === dominio.id ? draft.conducta : null });
    go('conducta');
  };

  // Guarda observación → cola offline-first → flush inmediato.
  const handleSaveObservation = async () => {
    if (!profile?.id || !profile?.colegio_id) {
      // Bloqueo defensivo: si el perfil no tiene colegio, no se puede crear observación.
      alert('Tu perfil aún no tiene colegio asignado. Avisa al Equipo INSIDE.');
      return;
    }
    const isOther = draft.conducta?.code === 'OTRA';
    if (isOther && !draft.evidencia?.nota?.trim()) return;

    const finalDraft = mode === 'rapido'
      ? {
          ...draft,
          curso: draft.curso || QUICK_DEFAULTS.curso,
          frecuencia: draft.frecuencia || QUICK_DEFAULTS.frecuencia,
          duracion: draft.duracion || QUICK_DEFAULTS.duracion,
          participantes: draft.participantes || QUICK_DEFAULTS.participantes,
          fuente: draft.fuente || QUICK_DEFAULTS.fuente,
        }
      : draft;

    const internal = calcularNivel(finalDraft.conducta, {
      intensidad: finalDraft.intensidad,
      frecuencia: finalDraft.frecuencia,
    });

    const clientId = draft.client_id || newUUID();
    const payload = buildObservationPayload({
      draft: finalDraft,
      mode,
      modoRegistro: appMode,
      profile,
      clientId,
    });

    // stagedFotos: entradas ya validadas en el modal AnonimizarFoto.
    // Cada una tiene { staging_path } (online) o { pending_data_url } (offline).
    // La imagen ORIGINAL nunca se persiste — sólo el resultado pixelado.
    const stagedFotos = (finalDraft.evidencia?.stagedFotos || []).map((s) => ({
      staging_path: s.staging_path || null,
      preview_url: s.preview_url || null,
      pending_data_url: s.pending_data_url || null,
      rostros_cliente: s.rostros_cliente ?? 0,
      rostros_servidor: s.rostros_servidor ?? 0,
      repixelado_servidor: !!s.repixelado_servidor,
      pending_server_verification: !!s.pending_server_verification,
    }));

    enqueueObservation({
      ...payload,
      _enqueued_evidencia: {
        nota: finalDraft.evidencia?.nota || '',
        stagedFotos,
      },
    });
    // El builder devuelve sólo el payload de obs; el queue acepta extra. Lo guardamos como item.evidencia.
    // Reformatemos el item: si quedó dentro, lo movemos correctamente.
    const queueItems = JSON.parse(localStorage.getItem('preventiapp_offline_queue_v1') || '[]');
    const myItem = queueItems.find((i) => i.payload.client_id === clientId);
    if (myItem) {
      myItem.evidencia = { nota: finalDraft.evidencia?.nota || '', stagedFotos };
      delete myItem.payload._enqueued_evidencia;
      localStorage.setItem('preventiapp_offline_queue_v1', JSON.stringify(queueItems));
    }

    trackEvent('mobile_observation_saved', {
      dominio: finalDraft.dominio?.id,
      conducta_code: finalDraft.conducta?.code,
      mode,
      app_mode: appMode,
      is_sentinel: internal.isSentinel,
      nivel_internal: internal.nivel,
      client_id: clientId,
    });

    setDraft({ ...finalDraft, client_id: clientId });
    go('confirmacion');

    // Disparar sync inmediato (si online) y refrescar Mis Registros tras sync.
    flush();
  };

  // Edición desde Mis Registros → trigger en BD crea la versión
  const handleUpdateObservacion = async (updated) => {
    try {
      const patch = {
        estudiante: updated.estudiante,
        contexto: updated.contexto,
        estado: updated.estado || 'editado',
      };
      if (updated.evidencia?.nota !== undefined) {
        // La nota actualizada se persiste como una nueva evidencia texto (auditoría)
        // pero por simplicidad de Etapa 6, mantenemos sólo el patch en observaciones.
      }
      await updateObservacion(updated.id, patch);
      if (updated.estado === 'marcado_erroneo') {
        await marcarErroneoApi(updated.id);
      }
      trackEvent('mobile_observation_updated', { id: updated.id, estado: updated.estado });
      reloadObservaciones();
    } catch (e) {
      console.error('[Etapa6] update obs:', e?.message || e);
      alert(`No se pudo actualizar el registro: ${e?.message || e}`);
      // Refrescar para revertir cualquier actualización optimista local al estado real del servidor
      reloadObservaciones();
    }
  };

  const resetDraft = () => setDraft({ ...EMPTY_DRAFT, client_id: null });
  const handleNuevaObservacion = () => { resetDraft(); go('home'); };
  const handleHome = () => { resetDraft(); go('home'); };
  const handleHistorial = () => go('mis-registros');

  const requestChangeAppMode = (next) => {
    if (next === appMode) return;
    setPendingAppMode(next);
  };
  const confirmChangeAppMode = () => {
    if (!pendingAppMode) return;
    trackEvent('mobile_app_mode_changed', { from: appMode, to: pendingAppMode });
    setAppMode(pendingAppMode);
    setPendingAppMode(null);
    resetDraft();
  };
  const cancelChangeAppMode = () => setPendingAppMode(null);

  const activeObservaciones = appMode === 'induccion' ? observacionesInduccion : observaciones;
  const isOther = draft.conducta?.code === 'OTRA';

  return (
    <div className="mobile-stage" data-testid="mobile-stage">
      <div className="absolute top-5 left-5 z-10 flex flex-col gap-2">
        <Link
          to="/sitio"
          data-testid="mobile-exit-link"
          aria-label="Ver sitio institucional"
          className="tap-44 inline-flex items-center gap-1.5 min-h-[44px] px-4 py-2 rounded-full bg-white/85 backdrop-blur border border-[#DDE6F7] text-brand-deep font-semibold text-xs hover:bg-white"
        >
          <ChevronLeft className="w-4 h-4" /> Ver sitio institucional
        </Link>
        <button
          onClick={signOut}
          data-testid="mobile-logout"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur border border-[#DDE6F7] text-brand-deep font-semibold text-[11px] hover:bg-white"
        >
          <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
        </button>
      </div>

      {/* Banner de estado de sincronización */}
      <div className="absolute top-5 right-5 z-10" data-testid="mobile-sync-status">
        {syncState.online ? (
          syncState.pendientes > 0 ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFF1D6] border border-[#F6B73C] text-[#7A4A0B] text-[11px] font-bold" data-testid="sync-pending">
              <CloudUpload className="w-3.5 h-3.5" /> Sincronizando {syncState.pendientes}…
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#DFF6EC] border border-[#1E9F73]/40 text-[#0F6A4A] text-[11px] font-bold" data-testid="sync-online">
              ● En línea
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FCDCDC] border border-[#E64545]/40 text-[#7C1F1F] text-[11px] font-bold" data-testid="sync-offline">
            <CloudOff className="w-3.5 h-3.5" /> Sin conexión · {syncState.pendientes} en cola
          </span>
        )}
      </div>

      <div className="phone-device" data-testid="phone-device">
        <div className={`phone-screen ${appMode === 'induccion' ? 'theme-induccion' : ''}`} data-app-mode={appMode}>
          <div className="phone-island" aria-hidden />
          <StatusBar time={time} />
          {appMode === 'induccion' && (
            <div className="induccion-banner" data-testid="induccion-banner" role="status">
              <p className="induccion-banner-title">
                <span aria-hidden>🟨</span> MODO INDUCCIÓN ACTIVO
              </p>
              <p className="induccion-banner-body">
                Utilice casos ficticios o anonimizados. <strong>No registre información sensible real de estudiantes.</strong> Las observaciones de este modo se usan únicamente para capacitación y no se incorporarán a los registros oficiales del establecimiento.
              </p>
            </div>
          )}
          <div className="phone-content" data-testid="phone-content">
            {screen === 'onboarding' && (
              <ScreenOnboarding onComplete={completeOnboarding} />
            )}
            {screen === 'home' && (
              <ScreenHome
                session={userSession}
                onSelectDomain={handleSelectDomain}
                onOpenHistorial={handleHistorial}
                draft={draft}
                mode={mode}
                onChangeMode={setMode}
                appMode={appMode}
                onRequestChangeAppMode={requestChangeAppMode}
                observacionesCount={activeObservaciones.length}
              />
            )}
            {screen === 'conducta' && draft.dominio && (
              <ScreenConducta
                dominio={draft.dominio}
                value={draft.conducta}
                setValue={(c) => updateDraft({ conducta: c })}
                onBack={() => go('home')}
                onNext={() => go('variables')}
                mode={mode}
              />
            )}
            {screen === 'variables' && draft.dominio && (
              <ScreenVariables
                dominio={draft.dominio}
                draft={draft}
                update={updateDraft}
                onBack={() => go('conducta')}
                onNext={() => go('evidencia')}
                mode={mode}
              />
            )}
            {screen === 'evidencia' && draft.dominio && (
              <ScreenEvidencia
                draft={draft}
                update={updateDraft}
                isOther={isOther}
                mode={mode}
                onBack={() => go('variables')}
                onNext={handleSaveObservation}
                onSkip={handleSaveObservation}
              />
            )}
            {screen === 'confirmacion' && draft.dominio && (
              <ScreenConfirmacion
                dominio={draft.dominio}
                draft={draft}
                session={userSession}
                mode={mode}
                onNueva={handleNuevaObservacion}
                onHome={handleHome}
              />
            )}
            {screen === 'mis-registros' && (
              <ScreenMisRegistros
                observaciones={activeObservaciones}
                session={userSession}
                onUpdateObservacion={handleUpdateObservacion}
                onBack={() => go('home')}
                loading={loadingObs}
              />
            )}
          </div>
          {pendingAppMode && (
            <div className="appmode-modal-overlay" data-testid="appmode-modal" role="dialog" aria-modal="true" aria-labelledby="appmode-modal-title">
              <div className="appmode-modal">
                <div className="appmode-modal-icon" aria-hidden>
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <h4 id="appmode-modal-title" className="appmode-modal-title">
                  {pendingAppMode === 'induccion' ? 'Activar Modo Inducción' : 'Volver a Registro Oficial'}
                </h4>
                <p className="appmode-modal-body">
                  {pendingAppMode === 'induccion'
                    ? 'Ahora estás en Modo Inducción. Utilice casos ficticios o anonimizados — no registre información sensible real de estudiantes. Las observaciones se usan únicamente para capacitación y nunca se mezclarán con los registros oficiales del establecimiento.'
                    : 'Ahora estás en Registro Oficial. Las observaciones registradas formarán parte del historial oficial del establecimiento y podrán ser gestionadas por el Equipo de Convivencia.'}
                </p>
                <div className="appmode-modal-actions">
                  <button
                    data-testid="appmode-cancel"
                    onClick={cancelChangeAppMode}
                    className="m-btn-ghost"
                    type="button"
                  >
                    Cancelar
                  </button>
                  <button
                    data-testid="appmode-confirm"
                    onClick={confirmChangeAppMode}
                    className="m-btn-primary"
                    type="button"
                    style={pendingAppMode === 'induccion'
                      ? { background: '#B97A0D', boxShadow: '0 12px 24px -12px rgba(185,122,13,0.55)' }
                      : { background: '#0B1B5E', boxShadow: '0 12px 24px -12px rgba(11,27,94,0.55)' }}
                  >
                    {pendingAppMode === 'induccion' ? 'Sí, activar Inducción' : 'Sí, volver a Oficial'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <aside className="mobile-sidepanel" data-testid="mobile-sidepanel">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#EEF4FF] border border-[#DDE6F7] text-xs font-bold text-brand-violet">
          <Info className="w-3.5 h-3.5" />
          Registro en terreno
        </div>
        <h3 className="mt-4 text-lg font-extrabold text-brand-deep leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          App móvil PreventiApp
        </h3>
        <p className="mt-2 text-sm text-brand-muted leading-relaxed">
          Captura de observaciones en terreno siguiendo el <strong>Observation Engine v1.0</strong>: 6 dominios oficiales, conductas observables, variables rápidas y eventos centinela.
        </p>

        <div className="mt-5 rounded-xl bg-white border border-[#DDE6F7] p-3 text-xs text-brand-deep">
          <p className="font-bold text-xs text-brand-muted">Usuario activo</p>
          <p className="mt-1 font-bold">{userSession.nombre}</p>
          <p className="text-[11px] text-brand-muted">Rol: {userSession.rol}</p>
          <p className="text-[11px] text-brand-muted">Colegio: {profile?.colegio_id ? '✓ asignado' : '⚠️ sin asignar'}</p>
        </div>

        <div className="mt-3 rounded-xl bg-[#EEF4FF] border border-[#DDE6F7] p-3 text-xs text-brand-deep leading-relaxed">
          Los registros se guardan de forma segura y funcionan incluso sin conexión. Cuando vuelves a estar en línea se sincronizan automáticamente.
        </div>

        <a
          href="/sitio"
          className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-brand-violet hover:underline"
        >
          Ir al sitio institucional <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </aside>
    </div>
  );
};

export default MobileApp;
